import React, { FC, useEffect } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { t } from "i18next";
import { useForm, Controller } from "react-hook-form";
import { CsobFormMetaDataDto, QuestionnaireType } from "@green0meter/api-client";
import { useNavigate } from "react-router-dom";

import { ExistingAssetWarning } from "./components";

import { Dialog } from "@components/common";
import { useCreateCsobForm, useUpdateCsobForm, useGetCsobFormsMetadata } from "@api";
import { Box, Button, Select, Typography } from "@components/base";
import { useUserStore } from "@store/user";

interface AssetQuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit?: boolean;
  formId?: string;
}

interface AssetQuestionnaireFormValues {
  assetName: string;
  questionnaireType: QuestionnaireType;
  description: string;
  groupAsset: boolean;
  hedgingAsset: boolean;
}

const AssetQuestionnaireModal: FC<AssetQuestionnaireModalProps> = ({
  isOpen,
  onClose,
  isEdit = false,
  formId,
}) => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { availableFormsMetadata } = useGetCsobFormsMetadata(user);
  const { createCsobForm, createCsobFormEnabled, isPendingCreateCsobForm } =
    useCreateCsobForm(user);
  const { updateForm, updateFormEnabled, isPendingUpdateForm } =
    useUpdateCsobForm(user);
  const metadata = availableFormsMetadata?.find((form) => form.formId === formId);
  const initialData = isEdit && formId ? metadata : undefined;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<AssetQuestionnaireFormValues>({
    defaultValues: {
      assetName: initialData?.name || "",
      questionnaireType:
        initialData?.questionnaireType || QuestionnaireType.EnergyAsset,
      description: initialData?.description || "",
      groupAsset: initialData?.isGroupAsset || false,
      hedgingAsset: initialData?.isHedgingAsset || false,
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({
        assetName: initialData.name,
        questionnaireType: initialData.questionnaireType,
        description: initialData.description || "",
        groupAsset: initialData.isGroupAsset,
        hedgingAsset: initialData.isHedgingAsset,
      });
    }
  }, [initialData, reset]);

  const onSubmit = (values: AssetQuestionnaireFormValues): void => {
    const data: CsobFormMetaDataDto = {
      questionnaireType: values.questionnaireType,
      name: values.assetName,
      description: values.description,
      isGroupAsset: values.groupAsset,
      isHedgingAsset: values.hedgingAsset,
    };

    if (isEdit && initialData && formId && metadata?.id) {
      updateForm(
        { id: metadata.id, data },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    } else {
      createCsobForm(data, {
        onSuccess: (res: CsobFormMetaDataDto) => {
          onClose();
          navigate(`/csob/form/${res}`);
        },
      });
    }
  };

  const isSubmitting = isEdit ? isPendingUpdateForm : isPendingCreateCsobForm;

  return (
    ((isEdit && updateFormEnabled) || (!isEdit && createCsobFormEnabled)) && (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title={
          isEdit
            ? t("csob.assetQuestionnaire.modalTitleEdit")
            : t("csob.assetQuestionnaire.modalTitle")
        }
      >
        <Box sx={{ p: 0 }}>
          <Typography variant="body1" gutterBottom>
            {t("csob.assetQuestionnaire.modalSubtitle")}
          </Typography>
          <ExistingAssetWarning onClose={onClose} isEdit={isEdit} />
          <TextField
            label={t("csob.assetQuestionnaire.assetName")}
            variant="outlined"
            fullWidth
            margin="normal"
            {...register("assetName", {
              required: t("csob.assetQuestionnaire.assetNameRequired"),
            })}
            error={!!errors.assetName}
            helperText={errors.assetName?.message}
          />
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel id="questionnaire-type-label">
              {t("csob.assetQuestionnaire.questionnaireType")}
            </InputLabel>
            <Select
              labelId="questionnaire-type-label"
              label={t("csob.assetQuestionnaire.questionnaireType")}
              defaultValue={
                initialData?.questionnaireType || QuestionnaireType.EnergyAsset
              }
              {...register("questionnaireType", {
                required: t("csob.assetQuestionnaire.questionnaireTypeRequired"),
              })}
              disabled={isEdit}
            >
              <MenuItem value={QuestionnaireType.EnergyAsset}>
                {t("csob.assetQuestionnaire.energyAssetQuestionnaireOption")}
              </MenuItem>
              <MenuItem value={QuestionnaireType.CommercialRealityAsset}>
                {t("csob.assetQuestionnaire.commercialRealityQuestionnaireOption")}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t("csob.assetQuestionnaire.detailedDescription")}
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            {...register("description")}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
            <Controller
              name="groupAsset"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label={
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2">
                        {t("csob.assetQuestionnaire.groupAsset")}
                      </Typography>
                      <Tooltip
                        title={t("csob.assetQuestionnaire.groupAssetTooltip")}
                      >
                        <InfoOutlinedIcon fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    </Box>
                  }
                />
              )}
            />
            <Controller
              name="hedgingAsset"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label={
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2">
                        {t("csob.assetQuestionnaire.hedgingAsset")}
                      </Typography>
                      <Tooltip
                        title={t("csob.assetQuestionnaire.hedgingAssetTooltip")}
                      >
                        <InfoOutlinedIcon fontSize="small" sx={{ ml: 1 }} />
                      </Tooltip>
                    </Box>
                  }
                />
              )}
            />
          </Box>
          <Box sx={{ textAlign: "right", mt: 3 }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              {isEdit
                ? t("csob.assetQuestionnaire.edit")
                : t("csob.assetQuestionnaire.start")}
            </Button>
          </Box>
        </Box>
      </Dialog>
    )
  );
};

export default AssetQuestionnaireModal;
